/**
 * Reads a consent-aware attribution block from first-party cookies.
 * Closed-loop attribution: gclid/gbraid + ga_client_id from lead to
 * offline conversion import. Ported from binder-forms src/utils/attribution.ts.
 *
 * - Statistics consent (Cookiebot) unlocks the GA bridge (_ga → ga_client_id).
 * - Marketing consent is passed in as a SNAPSHOT taken at submit time
 *   (same value that goes into the form POST), NOT read live from Cookiebot —
 *   avoids a race between consent change and submit.
 *
 * Cookie sources (written by GTM on binder-world.com):
 *   binder_attr (JSON): utm_* params + gclid/gbraid, first/last touch — wins.
 *   _gcl_aw / _gcl_gb:  fallback for gclid/gbraid.
 *   _ga:                ga_client_id (GA bridge).
 *
 * Fail-safe: all cookie parsing is wrapped; an error must never block the
 * submit. Only non-empty values are included; an empty block means the
 * caller omits the field entirely.
 */
export function getAttribution(marketingConsent) {
    const attr = {};
    try {
        if (typeof document === 'undefined') return attr;
        const cookie = document.cookie;

        // Statistics consent (Cookiebot) → GA bridge
        if (window.Cookiebot && window.Cookiebot.consent && window.Cookiebot.consent.statistics) {
            const gaClientId = (cookie.match(/_ga=GA\d\.\d\.(\d+\.\d+)/) || [])[1];
            if (gaClientId) attr.ga_client_id = gaClientId;
        }

        // Marketing consent → authoritative from the submit-time snapshot
        if (marketingConsent === 'GRANTED') {
            const m = cookie.match(/binder_attr=([^;]+)/);
            if (m) {
                try {
                    const parsed = JSON.parse(decodeURIComponent(m[1]));
                    for (const [key, value] of Object.entries(parsed)) {
                        if (value != null && value !== '') attr[key] = String(value);
                    }
                } catch (e) {
                    /* malformed binder_attr cookie — ignore */
                }
            }
            const gclid = attr.gclid || (cookie.match(/_gcl_aw=GCL\.\d+\.([\w-]+)/) || [])[1];
            if (gclid) attr.gclid = gclid;
            const gbraid = attr.gbraid || (cookie.match(/_gcl_gb=GCL\.\d+\.([\w-]+)/) || [])[1];
            if (gbraid) attr.gbraid = gbraid;
        }
    } catch (e) {
        /* fail-safe: attribution must never block the submit */
    }
    return attr;
}

/**
 * Snapshot of the Cookiebot marketing consent at call time.
 * @returns {'GRANTED'|'DENIED'}
 */
export function getMarketingConsent() {
    try {
        return window.Cookiebot && window.Cookiebot.consent && window.Cookiebot.consent.marketing
            ? 'GRANTED'
            : 'DENIED';
    } catch (e) {
        return 'DENIED';
    }
}
